"use client";

import { useId, useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import { useToolNumber } from "@/components/tools/tool-ui";
import { downloadPng } from "@/lib/tool-kit/download";
import { linearScale, niceStep, ticksBetween } from "@/lib/tool-kit/math/plot";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/*
 * The complex plane: real axis across, imaginary axis up, equal units on both. Points are
 * drawn with an arrow from the origin; a circle and a polygon can be added for roots.
 * Colours are attributes, so the PNG download matches the page.
 */
export interface ArgandPoint {
  re: number;
  im: number;
  label: string;
}

const SIZE = 380;
const MARGIN = 34;
const UNSAFE_ID = /[^a-zA-Z0-9_-]/g;

export function ArgandDiagram({
  points,
  radius,
  polygon = false,
  title,
  downloadLabel,
  realLabel,
  imaginaryLabel,
}: {
  points: ArgandPoint[];
  radius?: number;
  polygon?: boolean;
  title: string;
  downloadLabel: string;
  realLabel: string;
  imaginaryLabel: string;
}) {
  const fmt = useToolNumber();
  const ref = useRef<SVGSVGElement>(null);
  const uid = useId().replace(UNSAFE_ID, "");
  const reach = Math.max(
    1,
    radius ?? 0,
    ...points.flatMap((p) => [Math.abs(p.re), Math.abs(p.im)])
  );
  const bound = reach * 1.3;
  const x = linearScale(-bound, bound, MARGIN, SIZE - MARGIN);
  const y = linearScale(-bound, bound, SIZE - MARGIN, MARGIN);
  const step = niceStep(bound * 2, 6);
  const ticks = ticksBetween(-bound, bound, step).filter((v) => v !== 0);
  const tick = (value: number) =>
    fmt(value, { maximumFractionDigits: 3 }).replace("-", "−");
  const ring = points.map((p) => `${x(p.re)},${y(p.im)}`).join(" ");
  return (
    <div className="space-y-3">
      <svg
        aria-labelledby={`arg-${uid}`}
        className="h-auto w-full max-w-md"
        fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
        ref={ref}
        role="img"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <title id={`arg-${uid}`}>{title}</title>
        <rect fill={CHART_INK.background} height={SIZE} width={SIZE} />
        {ticks.map((value) => (
          <g key={value}>
            <line
              stroke={CHART_INK.grid}
              x1={x(value)}
              x2={x(value)}
              y1={MARGIN}
              y2={SIZE - MARGIN}
            />
            <line
              stroke={CHART_INK.grid}
              x1={MARGIN}
              x2={SIZE - MARGIN}
              y1={y(value)}
              y2={y(value)}
            />
            <text
              fill={CHART_INK.muted}
              fontSize={10}
              textAnchor="middle"
              x={x(value)}
              y={y(0) + 14}
            >
              {tick(value)}
            </text>
            <text
              dominantBaseline="middle"
              fill={CHART_INK.muted}
              fontSize={10}
              textAnchor="end"
              x={x(0) - 5}
              y={y(value)}
            >
              {tick(value)}
            </text>
          </g>
        ))}
        <line
          stroke={CHART_INK.text}
          strokeWidth={1.25}
          x1={MARGIN}
          x2={SIZE - MARGIN}
          y1={y(0)}
          y2={y(0)}
        />
        <line
          stroke={CHART_INK.text}
          strokeWidth={1.25}
          x1={x(0)}
          x2={x(0)}
          y1={MARGIN}
          y2={SIZE - MARGIN}
        />
        <text
          fill={CHART_INK.text}
          fontSize={12}
          fontStyle="italic"
          textAnchor="end"
          x={SIZE - MARGIN}
          y={y(0) - 6}
        >
          {realLabel}
        </text>
        <text
          fill={CHART_INK.text}
          fontSize={12}
          fontStyle="italic"
          x={x(0) + 6}
          y={MARGIN - 8}
        >
          {imaginaryLabel}
        </text>
        {radius === undefined ? null : (
          <circle
            cx={x(0)}
            cy={y(0)}
            fill="none"
            r={x(radius) - x(0)}
            stroke={CHART_INK.axis}
            strokeDasharray="5 4"
          />
        )}
        {polygon ? (
          <polygon
            fill="none"
            points={ring}
            stroke={CHART_COLORS[4]}
            strokeWidth={1.5}
          />
        ) : null}
        {points.map((p, index) => {
          const color = CHART_COLORS[[0, 3, 2, 5][index % 4]];
          const near = x(p.re) > SIZE - 90;
          return (
            <g key={`${p.label}-${index}`}>
              {polygon ? null : (
                <line
                  stroke={color}
                  strokeWidth={2}
                  x1={x(0)}
                  x2={x(p.re)}
                  y1={y(0)}
                  y2={y(p.im)}
                />
              )}
              <circle
                cx={x(p.re)}
                cy={y(p.im)}
                fill={color}
                r={4.5}
                stroke={CHART_INK.background}
                strokeWidth={1.5}
              />
              <text
                fill={CHART_INK.text}
                fontSize={11}
                textAnchor={near ? "end" : "start"}
                x={near ? x(p.re) - 8 : x(p.re) + 8}
                y={y(p.im) + (p.im >= 0 ? -8 : 16)}
              >
                {p.label}
              </text>
            </g>
          );
        })}
      </svg>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadPng("complex-plane.png", ref.current).catch(
              () => undefined
            );
          }
        }}
        type="button"
      >
        {downloadLabel}
      </button>
    </div>
  );
}
